import React, { useState, useEffect, useMemo, useRef } from "react";
import env from "../../../../config/env"
import Analytics from "./assets/Analytics.png";
import { Search, ExternalLink, X, Loader2, PlugZap, Pin } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "components/ui/tooltip";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { useUserInfo } from "../../../../context/user";
import AddUserModal from "../team/component/AddUserModal";
import { useUsersContext } from "../../../../context/usersContext";
import { useActionData, useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import { Toast } from "../../../../components/Toast/Toast";
import Nango from "@nangohq/frontend";
import UnsupportedToolDialog from "./UnsupportedToolDialog";
import TeamUsageStrip from "./TeamUsageStrip";
import {
  useAvailableIntegrations,
  useIntegrationConnectionPoll,
  useCreateNangoConnectSession,
  useStoreNangoSession,
  useDisconnectIntegration,
  useDisconnectGhl,
} from "../../../../queries/integrations";

const favicon = (domain) =>
  `https://www.google.com/s2/favicons?sz=64&domain_url=${domain}`;

// Slack and MS Teams are core channels for the product, so their cards carry
// the pinned badge (Lovable's Your Tools page).
const PINNED_PROVIDERS = ["slack", "microsoft-teams"];


const INSTALL_REQUIRED_PROVIDERS = ["monday", "microsoft-teams"];


const needsInstallStep = (integration) =>
  integration?.show_install_button !== false &&
  INSTALL_REQUIRED_PROVIDERS.includes(integration?.provider?.toLowerCase());

const getInstallUrl = (integration) => {
  const provider = integration?.provider?.toLowerCase() || "";
  if (provider === "monday") return env.REACT_APP_MONDAY_URL || null;
  if (provider === "microsoft-teams") return env.REACT_APP_TEAMS_URL || null;
  return null;
};

/** Logo for a provider — hand-mapped where the favicon guess would miss. */
const getIntegrationLogo = (integration) => {
  const provider = integration?.provider?.toLowerCase() || "";
  if (provider === "google-analytics") return Analytics;
  if (provider === "fireflies") return favicon("fireflies.ai");
  if (provider === "roller") return favicon("https://mysupport.roller.software/");
  if (provider === "google-ads") return favicon("ads.google.com");
  if (provider === "facebook") return favicon("meta.com");
  if (provider === "microsoft-teams") return favicon("teams.live.com");
  if (provider.includes("fathom")) return favicon("fathom.ai");
  return favicon(`${provider.split("-")[0]}.com`);
};

/** Display name for a provider, with the one rebrand we carry. */
const getIntegrationLabel = (integration) =>
  integration?.provider?.toLowerCase() === "facebook"
    ? "Meta"
    : integration?.display_name || "";

const NangoIntegration = ({ locationId, userInfo }) => {
  const [error, setError] = useState(null);
  const [loadingProvider, setLoadingProvider] = useState(null);
  const [confirmDisconnect, setConfrimDisconnect] = useState(false);
  const [disconnectProvider, setDisconnectProvider] = useState({});
  const [disconnecting, setDisconnecting] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [requestTarget, setRequestTarget] = useState(null);
  const userActiveLocation = useUserInfo()[4];
  const saveActiveLocation = useUserInfo()[5];
  const [openTeamModal, setOpenTeamModal] = useState(false);
  const { products, locationUsers, agency, requestLocationUsers } = useUsersContext();

  useEffect(() => {
    requestLocationUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const activeProduct = products?.filter((item) => item.activePlan)?.[0];
  const navigate = useNavigate();
  const [toast, setToast] = useState({ message: '', subMessage: '', visible: false, })
  const NANGO_API_HOST = env.NANGO_API_HOST || "https://api.nango.dev";
  const NANGO_CONNECT_UI_HOST = env.NANGO_CONNECT_UI_HOST || "https://connect.nango.dev";
  const hasConnectedRef = useRef(false);

  const nango = new Nango({
    host: NANGO_API_HOST,
    apiURL: NANGO_API_HOST
  });

  const createSessionMutation = useCreateNangoConnectSession();
  const storeSessionMutation = useStoreNangoSession();
  const disconnectMutation = useDisconnectIntegration();
  const disconnectGhlMutation = useDisconnectGhl();

  const launchOAuthPopup = async (integration) => {
    setLoadingProvider(integration);
    setError(null);

    try {
      const sessionResponse = await createSessionMutation.mutateAsync({
        userInfo,
        allowedIntegrations: [integration.integration_id || integration.provider],
      });

      if (!sessionResponse?.success || !sessionResponse?.data?.token) {
        throw new Error(
          sessionResponse?.message ||
          sessionResponse?.error ||
          "Failed to get session token"
        );
      }

      const sessionToken = sessionResponse.data.token;
      const connect = nango.openConnectUI({
        baseURL: NANGO_CONNECT_UI_HOST,
        apiURL: NANGO_API_HOST,
        onEvent: async (event) => {
          if (event.type === "close") {
            setLoadingProvider(null);
            if (!hasConnectedRef.current)
              setError({
                [integration.provider]: "Connection flow was closed"
              });
          } else if (event.type === "connect") {
            try {
              const { connectionId } = event.payload;

              const sendConnectionToBackend = await storeSessionMutation.mutateAsync({
                provider: integration.provider,
                connection_id: connectionId,
                profile_id: locationId,
                user_id: userInfo?.userId,
                owner_id: userInfo?._id,
                agency_id: userActiveLocation?.agency_id,
                ...(integration?.integration_id ? { integration_id: integration.integration_id } : {}),
              });
              if (sendConnectionToBackend?.success) {
                hasConnectedRef.current = true;
                setLoadingProvider(null);
                fetchIntegrations();
              }
              else {
                setLoadingProvider(null);
                setError({
                  [integration.provider]:
                    sendConnectionToBackend?.message || "Failed to store connection"
                });
              }
            } catch (err) {
              setLoadingProvider(null);
              setError({
                [integration.provider]: "Something went wrong while saving connection"
              });
            }
          }
        },
      });

      connect.setSessionToken(sessionToken);
    } catch (err) {
      console.error("Nango connection error:", err);
      setError({ [integration.provider]: err.response?.data?.message || err.message || "Failed to connect" });
      setLoadingProvider(null);
    }
    setTimeout(() => { setError(null) }, 3000)
  };

  const currentURL = window.location.href;
  const urlObject = new URL(currentURL);
  const domain = urlObject.hostname;

  const {
    data: availableData,
    isLoading: waiting,
    refetch: fetchIntegrations,
  } = useAvailableIntegrations(locationId, userInfo?.userId, userActiveLocation?.agency_id, {
    enabled: !!userActiveLocation?.agency_id,
  });
  const integrations = availableData?.integrations ?? [];
  const connectedInt = availableData?.connected ?? [];

  // Fallback confirmation for a connect in flight: the Nango SDK's onEvent
  // "connect" callback above is the primary path (it fires fetchIntegrations
  // itself), this just catches the case where that event never arrives even
  // though the connection went through server-side. Polls only while
  // loadingProvider is set, and React Query tears the interval down the
  // instant it's cleared — replaces the old plain-`let` connectionInterval
  // that never got cleared on unmount.
  const { data: locationPoll } = useIntegrationConnectionPoll(locationId, !!loadingProvider);
  useEffect(() => {
    if (locationPoll?.data?.nango_connected) {
      setLoadingProvider(null);
      fetchIntegrations();
    }
  }, [locationPoll]);

  const handleDisconnection = async () => {
    setDisconnecting(true);
    console.log("disconnectProvider", disconnectProvider);
    // return
    try {
      // owner_id mirrors the connect call: anything provisioned for this user
      // (the managed Teams webhook) is keyed on the Mongo _id, not on user_id.
      await disconnectMutation.mutateAsync({
        profileId: locationId,
        provider: disconnectProvider?.provider,
        userId: userInfo?.userId,
        ownerId: userInfo?._id,
        integrationId: disconnectProvider?.integration_id,
      });
      setDisconnectProvider({});
      setDisconnecting(false);
      setConfrimDisconnect(false);
      fetchIntegrations();
    } catch (error) {
      console.error("Failed to fetch integrations:", error);
      setError("Failed to fetch integrations.");
    }
  };

  // True while a connect/disconnect is in flight for this integration. Same
  // condition the CTA labels used inline before; extracted because it is read
  // from six places.
  const isProviderPending = (integration) =>
    Boolean(
      (loadingProvider?.integration_id &&
        loadingProvider?.integration_id === integration?.integration_id &&
        loadingProvider?.provider === integration?.provider) ||
      (!loadingProvider?.integration_id &&
        loadingProvider?.provider === integration?.provider),
    );

  // Feeds the strip above the grid: one pill per connected provider, with the
  // teammates who connected it stacked as avatars.
  const teamUsageTools = useMemo(() => {
    const byProvider = new Map();
    (connectedInt || []).forEach((item) => {
      const provider = item?.provider;
      if (!provider) return;
      const entry = byProvider.get(provider) || { provider, teammates: [] };
      const name = (item?.userName || "").trim();
      if (name && !entry.teammates.includes(name)) entry.teammates.push(name);
      byProvider.set(provider, entry);
    });
    return [...byProvider.values()]
      .map(({ provider, teammates }) => {
        const integration = integrations.find(
          (item) => item?.provider === provider,
        );
        if (!integration) return null;
        return {
          name: getIntegrationLabel(integration),
          searchTerm: (integration?.display_name || provider).toLowerCase(),
          logoUrl: getIntegrationLogo(integration),
          teammates,
        };
      })
      .filter(Boolean)
      .sort(
        (a, b) =>
          b.teammates.length - a.teammates.length ||
          a.name.localeCompare(b.name),
      );
  }, [connectedInt, integrations]);

  const getConnectedIntegration = (integration) => {
    const connected = connectedInt.find((item) => item.provider === integration?.provider);
    if (integration?.integration_id && connected && integration?.integration_id === connected?.integration_id) {
      return connected
    }
    if (!integration?.integration_id && connected) {
      return connected
    }
    return null
  };

  const handleGHLLocationRefresh = () => {
    const scope =
      "users.readonly workflows.readonly contacts.readonly contacts.write locations/customFields.readonly locations/customFields.write businesses.readonly businesses.write calendars.readonly calendars.write calendars/events.readonly calendars/events.write calendars/groups.readonly calendars/groups.write calendars/resources.readonly calendars/resources.write campaigns.readonly conversations.readonly conversations/message.readonly conversations.write conversations/message.write conversations/reports.readonly objects/schema.readonly objects/schema.write objects/record.readonly objects/record.write courses.write courses.readonly forms.readonly forms.write invoices.readonly invoices.write invoices/schedule.readonly invoices/schedule.write invoices/template.readonly invoices/template.write links.readonly lc-email.readonly links.write locations.readonly locations/customValues.readonly locations/customValues.write locations/tasks.readonly locations/tasks.write locations/tags.readonly locations/tags.write locations/templates.readonly medias.readonly medias.write funnels/redirect.readonly funnels/page.readonly funnels/funnel.readonly funnels/pagecount.readonly funnels/redirect.write opportunities.readonly opportunities.write payments/orders.readonly payments/orders.write payments/integration.readonly payments/integration.write payments/transactions.readonly payments/subscriptions.readonly payments/custom-provider.readonly payments/custom-provider.write products.readonly products.write products/prices.readonly products/prices.write products/collection.readonly products/collection.write socialplanner/oauth.readonly socialplanner/oauth.write socialplanner/post.readonly socialplanner/post.write socialplanner/account.readonly socialplanner/account.write socialplanner/csv.readonly socialplanner/csv.write socialplanner/category.readonly socialplanner/tag.readonly store/shipping.readonly store/shipping.write store/setting.readonly store/setting.write surveys.readonly users.write blogs/post.write blogs/post-update.write blogs/check-slug.readonly blogs/category.readonly blogs/author.readonly";

    const refreshUrl = `https://marketplace.gohighlevel.com/oauth/chooselocation?response_type=code&redirect_uri=https://${domain}/app-integrations/location&client_id=${env.GHL.CLIENT_ID}&scope=${scope}&state=${locationId}`;

    window.open(refreshUrl, "_blank");
  };

  const handleDisconnectGHL = async () => {
    setDisconnecting(true)
    const response = await disconnectGhlMutation.mutateAsync(userActiveLocation?._id);

    if (response?.success) {
      saveActiveLocation(response?.data)
      setDisconnecting(false);
      setConfrimDisconnect(false)
      setDisconnectProvider({})
    }

    if (!response?.success) {
      setDisconnecting(false);
      setConfrimDisconnect(false)
      setDisconnectProvider({})
      setToast({ message: response?.message, visible: true, subMessage: "" })
    }

  }

  return (
    <>
      <div className="page-header [&_.page-lede]:text-sm pb-4">
        <span className="page-eyebrow">Configure</span>
        <h1 className="page-display text-3xl md:text-4xl">
          Your Tools.{" "}
          <span className="accent block md:inline">Connect once, delegate anywhere.</span>
        </h1>
        <p className="page-lede">
          Bring your tools into Maybe* so your Tasks can read, write, and act on your behalf. Need a hand connecting something?{" "}
          <button
            type="button"
            onClick={() => navigate("/app/settings", { state: { tab: "Team" } })}
            className="underline decoration-border underline-offset-2 hover:text-foreground hover:decoration-foreground/40"
          >
            Invite a team member
          </button>{" "}
          to help.
        </p>
      </div>

      <TeamUsageStrip
        tools={teamUsageTools}
        onSelect={(term) => setSearchQuery(term)}
      />

      {/* bg-card, not the default field grey: Lovable forces the search field
          pure white, and --card is white in light mode (and theme-correct in
          dark, which a literal bg-white would not be). */}
      <div className="interactive-field relative mb-2 max-w-3xl h-9 flex items-center rounded-md bg-card">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
        <Input
          type="text"
          className="border-0 bg-transparent h-full !pl-8 w-full text-sm focus-visible:ring-0 focus-visible:ring-offset-0"
          placeholder="Search integrations"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value?.toLowerCase())}
        />
      </div>

      <div className="mb-8 max-w-3xl min-h-[24px]">
        {searchQuery.trim() &&
          searchQuery.trim() !== "instagram" &&
          !integrations.some(
            (i) =>
              i?.display_name?.toLowerCase() === searchQuery.trim() ||
              i?.provider?.toLowerCase() === searchQuery.trim()
          ) && (
            <button
              type="button"
              onClick={() => setRequestTarget(searchQuery.trim())}
              className="inline-flex items-center gap-1.5 rounded-full border border-dashed border-border bg-muted/40 px-2.5 py-1 text-[11px] text-muted-foreground transition-colors hover:bg-background hover:border-foreground/40 hover:text-foreground"
            >
              <PlugZap className="h-3 w-3" />
              Request "{searchQuery.trim()}"
            </button>
          )}
      </div>

      <UnsupportedToolDialog
        tool={requestTarget}
        open={requestTarget !== null}
        onOpenChange={(o) => !o && setRequestTarget(null)}
      />

      {/* Lovable's tool grid: equal-height rows, one column on the narrowest
          phones, then 2 / 3 / 4 / 5 as the viewport grows. */}
      <div className="grid auto-rows-fr items-stretch gap-3 sm:gap-4 xl:gap-5 grid-cols-1 min-[420px]:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 min-[1600px]:grid-cols-5">
        {!waiting ? (
          <>
            {integrations
              ?.filter((item) => {
                const providerName = item?.provider?.toLowerCase();
                const displayName = item?.display_name?.toLowerCase();

                // If searching for "instagram", show Meta/Facebook integration
                if (
                  searchQuery === "instagram" &&
                  providerName === "facebook"
                ) {
                  return true;
                }

                // Default search by provider name or display name
                return (
                  providerName?.includes(searchQuery) ||
                  displayName?.includes(searchQuery)
                );
              })
              ?.map((integration) => (
                <div
                  className="interactive-surface relative rounded-xl bg-card p-4 sm:p-5 flex h-full min-h-[280px] sm:min-h-[292px] lg:min-h-[300px] flex-col"
                  key={integration.provider}
                >
                  {PINNED_PROVIDERS.includes(
                    integration?.provider?.toLowerCase(),
                  ) && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span
                              aria-label="Pinned. Core channel for Maybe*."
                              className="absolute top-3 right-3 inline-flex h-6 w-6 items-center justify-center rounded-full bg-muted text-muted-foreground"
                            >
                              <Pin className="h-3 w-3" />
                            </span>
                          </TooltipTrigger>
                          <TooltipContent side="left" className="max-w-xs">
                            <p className="text-xs leading-relaxed">
                              Pinned. Slack and Teams are core to Maybe*.
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  {/* Status pill centered at top */}
                  <div className="flex h-7 justify-center">
                    {getConnectedIntegration(integration)?.provider === integration?.provider ? (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                        Connected
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-muted text-muted-foreground">
                        Disconnected
                      </span>
                    )}
                  </div>

                  {/* Logo + name */}
                  <div className="mt-3 sm:mt-4 flex min-h-[40px] sm:min-h-[44px] items-center gap-3">
                    <div className="h-9 w-9 sm:h-10 sm:w-10 rounded-lg bg-background border border-border flex items-center justify-center overflow-hidden shrink-0">
                      <img
                        src={getIntegrationLogo(integration)}
                        alt={integration?.display_name}
                        className="h-6 w-6 sm:h-7 sm:w-7 object-contain"
                        onError={(event) => {
                          event.currentTarget.style.display = "none";
                        }}
                      />
                    </div>
                    <h3
                      className="font-semibold text-foreground text-[15px] sm:text-base leading-tight line-clamp-2 break-words min-w-0"
                      title={getIntegrationLabel(integration)}
                    >
                      {getIntegrationLabel(integration)}
                    </h3>
                  </div>

                  {/* Description + learn more */}
                  <div className="mt-3 sm:mt-4">
                    <p className="text-[13px] sm:text-sm text-foreground line-clamp-1">
                      Connect{getConnectedIntegration(integration)?.provider === integration?.provider ? "ed" : ""} to{" "}
                      {integration.provider === "google-drive"
                        ? "Google (Docs, Sheets, Slides)"
                        : integration?.provider?.toLowerCase() === "facebook"
                          ? "Meta"
                          : integration.display_name}
                    </p>
                    {integration?.article_url && (
                      <a
                        href={integration.article_url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mt-1"
                      >
                        Learn more <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </div>

                  {/* Account row (pinned to the card bottom so the dot row +
                      action button align across every card regardless of
                      whether a "Learn more" link is present above) */}
                  {getConnectedIntegration(integration)?.provider === integration?.provider ? (
                    <div className="mt-auto flex items-center gap-2 pt-4 text-[13px] sm:text-sm">
                      <span className="h-2 w-2 shrink-0 rounded-full bg-emerald-500" />
                      <span
                        title={getConnectedIntegration(integration)?.userName ?? ""}
                        className="line-clamp-1 min-w-0 text-foreground capitalize"
                      >
                        {getConnectedIntegration(integration)?.userName ?? ""}
                      </span>
                    </div>
                  ) : (
                    <div className="mt-auto flex items-center gap-2 pt-4 text-[13px] sm:text-sm">
                      <span className="h-2 w-2 shrink-0 rounded-full bg-rose-300" />
                      <span className="line-clamp-1 min-w-0 text-muted-foreground capitalize">
                        No Account Found
                      </span>
                    </div>
                  )}

                  {/* Action zone — Lovable reserves the same height on every
                      card, so the CTA row lines up across the grid. */}
                  <div className="mt-3 sm:mt-4 flex flex-col gap-2">
                    <div key={integration.provider}>
                      {needsInstallStep(integration) ? (
                        <div className="flex items-stretch gap-2 min-w-0">
                          {getConnectedIntegration(integration)?.provider ===
                            integration?.provider ? (
                            <Button
                              variant="outline"
                              onClick={() => {
                                setConfrimDisconnect(true);
                                setDisconnectProvider({
                                  provider: integration.provider,
                                  name: integration?.display_name,
                                  integration_id: integration?.integration_id,
                                });
                              }}
                              title={`Disconnect ${integration.display_name}`}
                              className="flex-1 min-w-0 h-10 rounded-md px-2 sm:px-3 text-[11px] sm:text-xs uppercase tracking-wider font-semibold"
                            >
                              {isProviderPending(integration)
                                ? "Disconnecting…"
                                : "Disconnect"}
                            </Button>
                          ) : (
                            <>
                              <Button
                                variant="outline"
                                onClick={() => {
                                  const installUrl = getInstallUrl(integration);
                                  if (installUrl) {
                                    window.open(installUrl, "_blank");
                                    return;
                                  }
                                  setError({
                                    [integration.provider]: `Install URL for ${integration.display_name} is not configured.`,
                                  });
                                }}
                                title={`Install the Maybe* app in ${integration.display_name}`}
                                className="interactive-surface flex-1 min-w-0 h-10 flex-col gap-0 rounded-md px-2 sm:px-3 bg-background"
                              >
                                <span className="font-mono text-[9px] uppercase tracking-wider text-muted-foreground">
                                  Step 1
                                </span>
                                <span className="text-[11px] uppercase tracking-wider font-semibold">
                                  Install app
                                </span>
                              </Button>
                              <Button
                                onClick={() => launchOAuthPopup(integration)}
                                disabled={isProviderPending(integration)}
                                title={`Connect ${integration.display_name}`}
                                className="flex-1 min-w-0 h-10 flex-col gap-0 rounded-md px-2 sm:px-3 bg-foreground text-background hover:bg-foreground/90"
                              >
                                <span className="font-mono text-[9px] uppercase tracking-wider text-background/70">
                                  Step 2
                                </span>
                                <span className="text-[11px] uppercase tracking-wider font-semibold">
                                  {isProviderPending(integration)
                                    ? "Connecting…"
                                    : "Connect"}
                                </span>
                              </Button>
                            </>
                          )}
                        </div>
                      ) : getConnectedIntegration(integration)?.provider ===
                        integration?.provider ? (
                        <Button
                          variant="outline"
                          onClick={() => {
                            setConfrimDisconnect(true);
                            setDisconnectProvider({
                              provider: integration.provider,
                              name: integration?.display_name,
                              integration_id: integration?.integration_id,
                            });
                          }}
                          title={`Disconnect ${integration.display_name}`}
                          className="w-full h-10 rounded-md px-2 sm:px-3 text-[11px] sm:text-xs uppercase tracking-wider font-semibold"
                        >
                          {isProviderPending(integration)
                            ? "Disconnecting…"
                            : "Disconnect"}
                        </Button>
                      ) : (
                        <Button
                          onClick={() => launchOAuthPopup(integration)}
                          disabled={isProviderPending(integration)}
                          title={`Connect ${integration.display_name}`}
                          className="w-full h-10 rounded-md px-2 sm:px-3 text-[11px] sm:text-xs uppercase tracking-wider font-semibold bg-foreground text-background hover:bg-foreground/90"
                        >
                          {isProviderPending(integration)
                            ? "Connecting…"
                            : "Connect"}
                        </Button>
                      )}
                    </div>
                    {Object.keys(error || {}).length > 0
                      ? <p className="text-destructive text-xs mt-1">{error?.[integration.provider]}</p>
                      : <p className="text-destructive text-xs mt-1">{error}</p>}
                  </div>
                </div>
              ))}
            {/* GHL Location Refresh Static Box */}
            <div className="interactive-surface relative rounded-xl bg-card p-4 sm:p-5 flex h-full min-h-[280px] sm:min-h-[292px] lg:min-h-[300px] flex-col">
              <div className="flex h-7 justify-center">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
                  GHL System
                </span>
              </div>
              <div className="mt-3 sm:mt-4 flex min-h-[40px] sm:min-h-[44px] items-center gap-3">
                <div className="h-9 w-9 sm:h-10 sm:w-10 rounded-lg bg-background border border-border flex items-center justify-center overflow-hidden shrink-0">
                  <img
                    src="https://www.google.com/s2/favicons?sz=64&domain_url=gohighlevel.com"
                    alt="GoHighLevel"
                    className="h-6 w-6 sm:h-7 sm:w-7 object-contain"
                  />
                </div>
                <h3 className="font-semibold text-foreground text-[15px] sm:text-base leading-tight line-clamp-2 break-words min-w-0">
                  GoHighLevel
                </h3>
              </div>

              <div className="mt-3 sm:mt-4">
                <p className="text-[13px] sm:text-sm text-foreground line-clamp-1">
                  Refresh GHL Location Data
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Sync latest location information and settings
                </p>
                <a
                  href={'https://www.notion.so/maybetech/How-to-integrate-connect-GoHighLevel-with-your-AI-Agents-and-what-the-integration-can-be-used-for-285e7fe9c4078020b063c52876f09622'}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mt-1"
                >
                  Learn more <ExternalLink className="h-3 w-3" />
                </a>
              </div>

              {userActiveLocation?.ghl_data && Object.keys(userActiveLocation?.ghl_data)?.length > 0 ? (
                <div className="mt-auto flex items-center gap-2 pt-4 text-[13px] sm:text-sm">
                  <span className="h-2 w-2 shrink-0 rounded-full bg-emerald-500" />
                  <span
                    title={userActiveLocation?.ghl_data?.business?.name}
                    className="line-clamp-1 min-w-0 text-foreground capitalize"
                  >
                    {userActiveLocation?.ghl_data?.business?.name}
                  </span>
                </div>
              ) : (
                <div className="mt-auto flex items-center gap-2 pt-4 text-[13px] sm:text-sm">
                  <span className="h-2 w-2 shrink-0 rounded-full bg-rose-300" />
                  <span className="line-clamp-1 min-w-0 text-muted-foreground capitalize">
                    No Account Found
                  </span>
                </div>
              )}

              <div className="mt-3 sm:mt-4 flex flex-col">
                {userActiveLocation?.ghl_data && Object.keys(userActiveLocation?.ghl_data)?.length > 0
                  ? <div className="flex flex-col gap-2">
                    <Button
                      onClick={handleGHLLocationRefresh}
                      className="w-full h-10 rounded-md px-2 sm:px-3 text-[11px] sm:text-xs uppercase tracking-wider font-semibold bg-foreground text-background hover:bg-foreground/90"
                    >
                      Refresh Location
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => { setConfrimDisconnect(true); setDisconnectProvider({ provider: "GHL", name: 'ghl' }) }}
                      className="w-full h-10 rounded-md px-2 sm:px-3 text-[11px] sm:text-xs uppercase tracking-wider font-semibold"
                    >
                      Disconnect
                    </Button>
                  </div> :
                  <Button
                    onClick={handleGHLLocationRefresh}
                    className="w-full h-10 rounded-md px-2 sm:px-3 text-[11px] sm:text-xs uppercase tracking-wider font-semibold bg-foreground text-background hover:bg-foreground/90"
                  >
                    Connect
                  </Button>
                }
              </div>
            </div>
          </>
        ) : (
          <p className="col-span-full text-sm text-muted-foreground">Loading integrations…</p>
        )}
        {!waiting && integrations.filter((item) => {
          const providerName = item?.provider?.toLowerCase();
          const displayName = item?.display_name?.toLowerCase();
          if (searchQuery === "instagram" && providerName === "facebook") return true;
          return providerName?.includes(searchQuery) || displayName?.includes(searchQuery);
        }).length === 0 && searchQuery && (
            <div className="empty-state col-span-full mt-2">
              <span className="empty-eyebrow">No matches</span>
              <p className="empty-title">No integrations match "{searchQuery}".</p>
              <p className="empty-lede">Try a different name, or request it above.</p>
            </div>
          )}
        {confirmDisconnect && (
          <div className="modal-zz" onClick={() => setConfrimDisconnect(true)}>
            <div
              className="modal-content-zz position-relative bg-card"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                aria-label="Close"
                disabled={disconnecting}
                className="absolute right-3 top-3 text-muted-foreground hover:text-foreground"
                onClick={() => setConfrimDisconnect(false)}
              >
                <X className="w-4 h-4" />
              </button>
              <div>
                <h2 className="text-xl font-bold">
                  Do you really want to disconnect {disconnectProvider?.name}?
                </h2>
              </div>
              <div style={{ display: "flex", gap: "20px", marginTop: "20px" }}>
                <Button
                  variant="outline"
                  onClick={() => setConfrimDisconnect(false)}
                  aria-label="Send message"
                  disabled={disconnecting}
                  className="w-full max-w-[300px] text-xs uppercase font-bold"
                >
                  <div className="hover-text">Cancel</div>
                </Button>

                <Button
                  variant="destructive"
                  onClick={disconnectProvider?.provider === 'GHL' ? handleDisconnectGHL : handleDisconnection}
                  className="w-full max-w-[300px] text-xs uppercase font-bold"
                  disabled={disconnecting}
                >
                  {disconnecting && (
                    <Loader2 className="animate-spin" />
                  )}
                  Disconnect
                </Button>
              </div>
            </div>
          </div>
        )}

        {openTeamModal && (
          <AddUserModal
            isModalOpen={openTeamModal}
            justCloseIt={() => setOpenTeamModal(false)}
            userActiveLocation={userActiveLocation}
            activeplans={activeProduct}
            locationUsers={locationUsers}
            setActiveTab={() =>
              navigate("/app/settings", { state: { tab: "Pricing" } })
            }
          />
        )}
        <Toast message={toast.message} visible={toast.visible} subMessage={toast.subMessage} onClose={() => setToast({ message: "", subMessage: "", visible: false })} />
      </div>
    </>
  );
};

export default NangoIntegration;
